import React, { createContext, useContext, useState, useEffect } from "react";
import api from "../service/api";
 
const AuthContext = createContext();

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error("useAuth must be used within an AuthProvider");
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  // State for editor count
  const [editorCount] = useState(0);

  useEffect(() => {
    // Check if user is logged in on app start
    const token = localStorage.getItem('adminToken');
    const userData = localStorage.getItem('adminUser');
    
    if (token && userData) {
      try {
        const parsedUser = JSON.parse(userData);
        if (parsedUser.role === 'ADMIN') {
          setUser(parsedUser);
          console.log('🔄 Restored user session from localStorage');
        } else {
          // Not an admin, clear storage
          localStorage.removeItem('adminToken');
          localStorage.removeItem('adminUser');
        }
      } catch (error) {
        console.error("Error parsing user data:", error);
        localStorage.removeItem("adminToken");
        localStorage.removeItem("adminUser");
      }
    }

    // Listen for automatic logout events (from 401 responses)
    const handleAutoLogout = () => {
      console.log('🔔 Auto-logout event received');
      setUser(null);
    };

    window.addEventListener('auth-logout', handleAutoLogout);

    setLoading(false);

    // Cleanup event listener
    return () => {
      window.removeEventListener('auth-logout', handleAutoLogout);
    };
  }, []);

 

  const login = async (identifier, password) => {
    try {
      const response = await api.post('/login', { identifier, password });
      console.log('Login response:', response.data);

      if (response.data.success && response.data.user) {
        const userData = response.data.user;
        console.log('User data from login:', userData);

        // Allow ADMIN, admin, and GENERAL roles
        if (
          userData.role !== "ADMIN" 
          ) {
          throw new Error("Access denied. Admin privileges required.");
        }

        // Store user data
        localStorage.setItem('adminUser', JSON.stringify(userData));
        localStorage.setItem('adminToken', 'cookie-based-auth');
        console.log('User data stored, updating state');

        setUser(userData);
        console.log('✅ Login successful - user state updated');
        return { success: true, user: userData };
      } else {
        throw new Error(response.data.message || "Login failed");
      }
    } catch (error) {
      console.error('Login error:', error);

      if (error.code === 'ERR_NETWORK' || error.message.includes('CORS')) {
        return {
          success: false,
          error:
            "Network error. Please check if the server is running and CORS is configured properly.",
        };
      }

      if (error.response?.status === 401) {
        return {
          success: false,
          error: 'Invalid identifier or password'
        };
      }

      return {
        success: false,
        error: error.response?.user?.message || error.message || "Login failed",
      };
    }
  };
  const logout = async () => {
    try {
      await api.get('/logout');
    } catch (error) {
      console.error('Logout error:', error);
    } finally {
      localStorage.removeItem('adminToken');
      localStorage.removeItem('adminUser');
      setUser(null);
      console.log('🧹 Local auth state cleared');
    }
  };

  const isOwner = (createdUserId) => {
    if (!user?._id || !createdUserId) return false;
    return String(user._id) === String(createdUserId);
  };

  const value = {
    user,
    editorCount,
     
    login,
    logout,
    loading,
    isAuthenticated: !!user,
    isAdmin: user?.role === "ADMIN",
    isOwner,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};
